import { RefObject, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, MicOff, Volume2, User, Shield, ShieldCheck, ShieldOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VideoDisplayProps {
  videoRef: RefObject<HTMLDivElement>;
  userVideoRef: RefObject<HTMLVideoElement>;
  isConnected: boolean;
  isSpeaking: boolean;
  isRecording: boolean;
}

export function VideoDisplay({
  videoRef,
  userVideoRef,
  isConnected,
  isSpeaking,
  isRecording,
}: VideoDisplayProps) {
  const userStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    const setupUserVideo = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });

        if (userVideoRef.current) {
          userVideoRef.current.srcObject = stream;
        }

        userStreamRef.current = stream;
      } catch (err) {
        console.error('Failed to get user video:', err);
      }
    };

    if (isConnected) {
      setupUserVideo();
    }

    return () => {
      if (userStreamRef.current) {
        userStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [isConnected, userVideoRef]);

  return (
    <div className="relative flex-1 bg-gray-900 overflow-hidden">
      {/* Main video */}
      <div className="absolute inset-0">
        {/* Video container - keep it separate from React's rendering */}
        <div
          ref={videoRef}
          className="absolute inset-0"
          style={{ zIndex: 1 }}
        />

        {/* Loading overlay */}
        {!isConnected && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ zIndex: 2 }}>
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="w-24 h-24 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="h-12 w-12 text-gray-500" />
              </div>

              <motion.div
                className="absolute inset-0 w-24 h-24 rounded-full border border-gray-600"
                animate={{
                  scale: [1, 1.3, 1.3],
                  opacity: [0.3, 0, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
              />
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-gray-400 text-sm"
            >
              Connecting to video session...
            </motion.p>
          </div>
          </div>
        )}
      </div>

      {/* Status indicators */}
      <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none">
        <div className="flex items-center space-x-2">
          {/* Connection Security */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center space-x-1.5 bg-gray-800/90 backdrop-blur px-3 py-1.5 rounded-lg border border-gray-700/50"
          >
            {isConnected ? (
              <>
                <ShieldCheck className="h-3.5 w-3.5 text-blue-500" />
                <span className="text-xs text-gray-300">Secure</span>
              </>
            ) : (
              <>
                <Shield className="h-3.5 w-3.5 text-gray-500" />
                <span className="text-xs text-gray-400">Connecting</span>
              </>
            )}
          </motion.div>

          {/* Speaking indicator */}
          <AnimatePresence>
            {isSpeaking && (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center space-x-1.5 bg-gray-800/90 backdrop-blur px-3 py-1.5 rounded-lg border border-gray-700/50"
              >
                <div className="flex space-x-0.5">
                  {[0, 1, 2].map((i) => (
                    <motion.div
                      key={i}
                      className="w-0.5 h-3 bg-blue-500 rounded-full"
                      animate={{
                        scaleY: [0.3, 1, 0.3],
                      }}
                      transition={{
                        duration: 0.6,
                        repeat: Infinity,
                        delay: i * 0.1,
                        ease: "easeInOut",
                      }}
                    />
                  ))}
                </div>
                <span className="text-xs text-gray-300">Speaking</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* User video PiP */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
        className={cn(
          "absolute bottom-4 right-4 w-40 h-30 bg-gray-900 rounded-xl overflow-hidden",
          "shadow-xl border border-gray-800",
          isRecording && "border-blue-500"
        )}
      >
        <video
          ref={userVideoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
        />

        <div className="absolute bottom-2 left-2">
          <AnimatePresence mode="wait">
            {isRecording ? (
              <motion.div
                key="recording"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center space-x-1 bg-blue-500/90 backdrop-blur px-2 py-1 rounded-md"
              >
                <motion.div
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                />
                <span className="text-xs text-white">Live</span>
              </motion.div>
            ) : (
              <motion.div
                key="muted"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center space-x-1 bg-gray-800/80 backdrop-blur px-2 py-1 rounded-md"
              >
                <MicOff className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-400">Muted</span>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

      </motion.div>

    </div>
  );
}